import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import { TYPOGRAPHY, PLACEHOLDER_IMAGES } from '../constants/typography';

const OurTeamPage = () => {
    const directorsData = [
        {
            id: 'aashish',
            name: 'Aashish Bohra',
            initials: 'AB',
            title: 'Partner – Havn Interior & Global Sourcing',
            role: 'Director – Purchase & Marketing',
            summary: 'A dynamic procurement strategist with 15+ years of experience. Expert in wood-finish materials, design development, and product innovation.',
            fullBio: `A dynamic procurement strategist and design-focused marketing leader, Aashish Bohra brings over 15 years of strong industry experience to Havn Interior & Global Sourcing. A graduate in Commerce, with academic roots at Vivekananda Engineering College, he has built a powerful foundation in material understanding, sourcing intelligence, and customer-centric design thinking.

Aashish is known for his deep technical knowledge of wood-finish materials such as Veneers, Laminates, and Natural Surface Solutions, along with exceptional skills in Design Development, Concept Creation, and Product Innovation. His unique ability to blend technical expertise with marketing creativity makes him a critical pillar in shaping Havn's high-value interior offerings.`,
            strengths: [
                'Strong understanding of wood and surface materials',
                'Innovation-driven design & concept development',
                'Technology-focused business functioning',
                'High customer communication & voice command presence',
                'Strategic sourcing and cost-competitive procurement'
            ],
            achievements: 'Aashish has earned multiple Awards and Recognitions from leading building-material pioneers such as Century, Greenply, and others—consistently achieving the highest sales targets and driving exceptional performance across all assigned regions.',
            closing: 'With his visionary approach and strong operational mastery, Aashish continues to elevate Havn Interior & Global Sourcing\'s product ecosystem, vendor partnerships, and market leadership.'
        },
        {
            id: 'kunal',
            name: 'Kunal I. Tater',
            initials: 'KT',
            title: 'Partner – Havn Interior & Global Sourcing',
            role: 'Director – Finance & Strategic Operations',
            summary: 'An analytical business leader with 15+ years of experience in finance, cost management, and strategic operations. Certified by Central Vigilance Commission.',
            fullBio: `An analytical, disciplined, and innovation-driven business leader, Kunal I. Tater serves as the Finance and Strategic Operations head at Havn Interior & Global Sourcing. Educated in Telecommunication Engineering from SRM Institute, Tamil Nadu, he brings a powerful blend of technical expertise, financial discipline, and design-driven operational understanding.

With over 15 years of diversified business experience, Kunal has played a key role in steering multiple ventures in Finance, Cost Management, Sales, Marketing, and Design Development. His ability to read markets, manage risk, optimise costs, and build strong operational frameworks makes him one of the core pillars of the organisation.`,
            strengths: [
                'Cost optimisation & financial planning',
                'Design development & product structuring',
                'Strategic sales & operational leadership',
                'Venture development and business scalability',
                'Technology-driven process improvements'
            ],
            achievements: 'Kunal is a certified professional recognized by the Central Vigilance Commission, honoured for demonstrating the Highest Standards of Honesty and Integrity—a rare and prestigious acknowledgement in India\'s corporate and governance ecosystem.',
            additional: `He also serves as a Director at RBSHAH Group (est. 1981), where he drives cost strategy, finance management, sales oversight, and new venture development across:

• EXIM Trade – Duty Credit Scrips
• Multi-brand Retail Stores
• Building & Interior Materials
• Emerging Business Sectors`,
            closing: 'With a sharp business mind and a principled leadership approach, Kunal continues to guide both Havn Interior & RBSHAH Group toward sustained growth, transparency, and future-ready expansion.'
        },
        {
            id: 'saargi',
            name: 'Saargi Jain',
            initials: 'SJ',
            title: 'Partner – Havn Interior & Global Sourcing',
            role: 'Director – Marketing & Design Development',
            summary: 'A creative design strategist with MBA in Finance and professional Interior Design training. National Award winner recognized by the Union Minister of Home Affairs.',
            fullBio: `A creative design strategist and marketing leader, Saargi Jain brings a powerful blend of aesthetics, functionality, and financial precision to Havn Interior & Global Sourcing. With an MBA in Finance (Ethiraj College, Chennai) and professional Interior Design training from NIIT Fashion, she delivers design solutions that balance modern elegance with cost efficiency.

With 5 years of specialised experience, Saargi excels in developing high-performance design concepts across:`,
            strengths: [
                'Kitchen Design & Modular Solutions',
                'Terrace & Outdoor Concepts',
                'Doors & Bathroom Design',
                'Complete Room Décor & Visual Styling'
            ],
            achievements: 'Known for her ability to resolve complex design challenges, calculate precise cost structures, and transform concepts into market-ready designs, she strengthens Havn\'s creative and customer experience leadership. Saargi is also a National Award winner, recognised for excellence in the Fastest Growing Indian Company category by the Union Minister of Home Affairs.',
            closing: 'Her design intelligence, analytical mindset, and customer-centric approach make her a vital pillar of Havn Interior & Global Sourcing\'s brand identity and innovation roadmap.'
        },
        {
            id: 'tarun',
            name: 'Tarun I. Jain',
            initials: 'TJ',
            title: 'President – RBSHAH Group',
            role: 'Partner – Havn Interior and Global Sourcing',
            summary: 'A visionary leader with 20+ years of multi-sector experience. National Award honoree recognized by Union Cabinet Minister.',
            fullBio: `With a legacy of leadership, innovation, and strategic growth, Tarun I. Jain stands at the forefront of RBSHAH Group's expansion into diverse and future-ready business verticals. A graduate in B.A. Economics from Loyola College, he brings over 20 years of multi-sector business experience, marked by strong collaborations, milestone achievements, and a deep understanding of modern business culture.

Tarun's journey is defined by a consistent pursuit of excellence. He is a National Award honoree, recognised by the Union Cabinet Minister of Petroleum & Natural Gas and the Lieutenant Governor of Puducherry. His impactful contributions have also earned accolades and certifications from leading PSUs and multinational organisations.`,
            rbshahResponsibilities: [
                'EXIM Trade & Duty Credit Scrips',
                'Multi-brand Retail Operations',
                'Building & Interior Material Solutions',
                'Expansion into New Ventures and Emerging Business Models'
            ],
            havnResponsibilities: [
                'Global sourcing partnerships',
                'Expansion of premium interior solutions',
                'Cost-efficient procurement frameworks',
                'Strategic collaborations across industries',
                'Development of new international business channels'
            ],
            closing: 'Driven by a forward-thinking mindset and a commitment to excellence, Tarun continues to position RBSHAH Group as a trusted, progressive, and innovation-led enterprise in India\'s evolving business landscape.'
        }
    ];

    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);

        // Simple fade-in animations without complex GSAP
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.opacity = '1';
                    entry.target.style.transform = 'translateY(0)';
                }
            });
        }, observerOptions);

        // Observe all sections
        document.querySelectorAll('.fade-in-section, .stagger-item, .section-title').forEach(el => {
            el.style.opacity = '0';
            el.style.transform = 'translateY(30px)';
            el.style.transition = 'all 0.8s ease';
            observer.observe(el);
        });

        // Cleanup
        return () => observer.disconnect();
    }, []);

    return (
        <div>
            <Helmet>
                <title>Our Team - Hav'n Interiors & Global Sourcing</title>
                <meta name="description" content="Meet the leadership team at Hav'n Interiors. Our experienced directors and core team members bring decades of expertise in interior design, finance, and strategic operations." />
                <meta name="keywords" content="interior design team, leadership, directors, Chennai design team, Hav'n Interiors team" />
            </Helmet>

            <Navbar />

            {/* Hero Section */}
            <section className="hero team-hero" style={{ 
                height: '70vh',
                background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600&q=80)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                position: 'relative',
                overflow: 'hidden',
                paddingTop: '120px'
            }}>
                <div className="hero-content" style={{ position: 'relative', zIndex: 2, textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)' }}>
                    <h1 className="team-hero-title" style={{ fontSize: '72px', fontWeight: '300', color: '#F5F1ED' }}>
                        Our Team
                    </h1>
                    <p className="team-hero-subtitle hero-subtitle" style={{ fontSize: '24px', color: '#F5F1ED', fontWeight: '400' }}>
                        Meet the Visionaries Behind Hav'n Interiors
                    </p>
                </div>
            </section>

            {/* Introduction */}
            <section className="fade-in-section" style={{ background: '#0F0F0F', padding: '50px 5%' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
                    <h2 style={{ ...TYPOGRAPHY.h2, marginBottom: '30px', color: 'var(--accent-color)' }}>
                        Leadership Excellence
                    </h2>
                    <p style={{ fontSize: '22px', lineHeight: '1.8', color: 'rgba(245, 241, 237, 0.9)', marginBottom: '40px', fontWeight: '300' }}>
                        Our team combines decades of experience in <strong style={{ color: 'var(--accent-color)', fontWeight: '500' }}>interior design, finance, strategic operations, and global sourcing</strong>. 
                        Together, we drive innovation and excellence in every project we undertake.
                    </p>
                </div>
            </section>

            {/* Board of Directors */}
            <section className="stagger-item" style={{ padding: '50px 5%' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <h2 className="section-title" style={{ 
                        fontSize: '48px', 
                        fontWeight: '200', 
                        textAlign: 'center', 
                        marginBottom: '80px', 
                        color: 'var(--accent-color)',
                        letterSpacing: '-1px'
                    }}>
                        Board of Directors
                    </h2>
                    
                    <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '50px'
                    }}>
                        {directorsData.map((director) => (
                            <div 
                                key={director.id}
                                className="stagger-child director-card" 
                                style={{
                                    background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.08) 0%, rgba(0, 0, 0, 0.5) 100%)',
                                    borderRadius: '20px',
                                    padding: '50px',
                                    border: '2px solid rgba(201, 169, 110, 0.3)',
                                    transition: 'all 0.4s ease'
                                }}
                            >
                                {/* Header Section */}
                                <div style={{ 
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '40px',
                                    marginBottom: '40px',
                                    paddingBottom: '30px',
                                    borderBottom: '2px solid rgba(201, 169, 110, 0.2)'
                                }}>
                                    <div style={{
                                        width: '120px',
                                        height: '120px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, var(--accent-color) 0%, #B8935A 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '48px',
                                        color: '#000',
                                        fontWeight: 'bold',
                                        flexShrink: 0
                                    }}>
                                        {director.initials}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <h4 style={{ 
                                            fontSize: '32px', 
                                            color: 'var(--accent-color)', 
                                            marginBottom: '10px',
                                            fontWeight: '300'
                                        }}>
                                            {director.name}
                                        </h4>
                                        <p style={{ 
                                            fontSize: '18px', 
                                            color: 'rgba(245, 241, 237, 0.9)', 
                                            marginBottom: '8px',
                                            fontWeight: '500'
                                        }}>
                                            {director.title}
                                        </p>
                                        <p style={{ 
                                            fontSize: '16px', 
                                            color: 'rgba(201, 169, 110, 0.9)', 
                                            fontStyle: 'italic'
                                        }}>
                                            {director.role}
                                        </p>
                                    </div>
                                </div>

                                {/* Full Bio */}
                                <div style={{ marginBottom: '30px' }}>
                                    {director.fullBio.split('\n\n').map((paragraph, index) => (
                                        <p key={index} style={{
                                            fontSize: '16px',
                                            color: 'rgba(245, 241, 237, 0.9)',
                                            lineHeight: '1.8',
                                            marginBottom: '20px',
                                            textAlign: 'justify'
                                        }}>
                                            {paragraph}
                                        </p>
                                    ))}
                                </div>

                                {/* Strengths/Expertise */}
                                {director.strengths && (
                                    <div style={{ marginBottom: '30px' }}>
                                        <h5 style={{
                                            fontSize: '20px',
                                            color: 'var(--accent-color)',
                                            marginBottom: '20px',
                                            fontWeight: '400'
                                        }}>
                                            {director.id === 'saargi' ? 'Areas of Expertise:' : 'Key Strengths:'}
                                        </h5>
                                        <ul style={{
                                            listStyle: 'none',
                                            padding: 0,
                                            margin: 0
                                        }}>
                                            {director.strengths.map((strength, index) => (
                                                <li key={index} style={{
                                                    fontSize: '16px',
                                                    color: 'rgba(245, 241, 237, 0.9)',
                                                    marginBottom: '12px',
                                                    paddingLeft: '30px',
                                                    position: 'relative',
                                                    lineHeight: '1.6'
                                                }}>
                                                    <span style={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        color: 'var(--accent-color)',
                                                        fontSize: '20px'
                                                    }}>
                                                        •
                                                    </span>
                                                    {strength}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* RBSHAH Responsibilities (Tarun) */}
                                {director.rbshahResponsibilities && (
                                    <div style={{ marginBottom: '30px' }}>
                                        <h5 style={{
                                            fontSize: '20px',
                                            color: 'var(--accent-color)',
                                            marginBottom: '20px',
                                            fontWeight: '400'
                                        }}>
                                            As President of RBSHAH Group (est. 1981), he spearheads strategic vision, alliances, and business development across:
                                        </h5>
                                        <ul style={{
                                            listStyle: 'none',
                                            padding: 0,
                                            margin: 0
                                        }}>
                                            {director.rbshahResponsibilities.map((item, index) => (
                                                <li key={index} style={{
                                                    fontSize: '16px',
                                                    color: 'rgba(245, 241, 237, 0.9)',
                                                    marginBottom: '12px',
                                                    paddingLeft: '30px',
                                                    position: 'relative',
                                                    lineHeight: '1.6'
                                                }}>
                                                    <span style={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        color: 'var(--accent-color)',
                                                        fontSize: '20px'
                                                    }}>
                                                        •
                                                    </span>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Havn Responsibilities (Tarun) */}
                                {director.havnResponsibilities && (
                                    <div style={{ marginBottom: '30px' }}>
                                        <h5 style={{
                                            fontSize: '20px',
                                            color: 'var(--accent-color)',
                                            marginBottom: '20px',
                                            fontWeight: '400'
                                        }}>
                                            At Havn, Tarun drives:
                                        </h5>
                                        <ul style={{
                                            listStyle: 'none',
                                            padding: 0,
                                            margin: 0
                                        }}>
                                            {director.havnResponsibilities.map((item, index) => (
                                                <li key={index} style={{
                                                    fontSize: '16px',
                                                    color: 'rgba(245, 241, 237, 0.9)',
                                                    marginBottom: '12px',
                                                    paddingLeft: '30px',
                                                    position: 'relative',
                                                    lineHeight: '1.6'
                                                }}>
                                                    <span style={{
                                                        position: 'absolute',
                                                        left: 0,
                                                        color: 'var(--accent-color)',
                                                        fontSize: '20px'
                                                    }}>
                                                        •
                                                    </span>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Additional Info (Kunal) */}
                                {director.additional && (
                                    <div style={{ marginBottom: '30px' }}>
                                        {director.additional.split('\n\n').map((paragraph, index) => (
                                            <div key={index}>
                                                {paragraph.split('\n').map((line, lineIndex) => (
                                                    <p key={lineIndex} style={{
                                                        fontSize: '16px',
                                                        color: 'rgba(245, 241, 237, 0.9)',
                                                        lineHeight: '1.8',
                                                        marginBottom: '8px',
                                                        textAlign: 'justify'
                                                    }}>
                                                        {line}
                                                    </p>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Achievements */}
                                {director.achievements && (
                                    <div style={{ 
                                        marginBottom: '30px',
                                        padding: '25px',
                                        background: 'rgba(201, 169, 110, 0.15)',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(201, 169, 110, 0.3)'
                                    }}>
                                        <p style={{
                                            fontSize: '16px',
                                            color: 'rgba(245, 241, 237, 0.95)',
                                            lineHeight: '1.8',
                                            textAlign: 'justify',
                                            margin: 0
                                        }}>
                                            {director.achievements}
                                        </p>
                                    </div>
                                )}

                                {/* Closing Statement */}
                                {director.closing && (
                                    <div style={{
                                        paddingTop: '25px',
                                        borderTop: '1px solid rgba(201, 169, 110, 0.2)'
                                    }}>
                                        <p style={{
                                            fontSize: '16px',
                                            color: 'rgba(245, 241, 237, 0.95)',
                                            lineHeight: '1.8',
                                            textAlign: 'justify',
                                            fontStyle: 'italic'
                                        }}>
                                            {director.closing}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Core Team Section */}
            

            {/* Footer */}
            <footer>
                <img src={`${process.env.PUBLIC_URL}/current-logo.png`} alt="Hav'n Interiors" style={{ width: '180px', marginBottom: '20px', opacity: 0.8 }} />
                <p>&copy; 2025 Hav'n Interiors & Global Sourcing. All Rights Reserved.</p>
                <p style={{ marginTop: '10px', fontSize: '12px', opacity: 0.6 }}>Innovate. Integrate. Execute.</p>
            </footer>

            {/* CSS for Hover Effects */}
            <style jsx>{`
                .core-team-card:hover .core-team-image {
                    transform: scale(1.1);
                }
            `}</style>
        </div>
    );
};


export default OurTeamPage;